import { useCallback, useRef } from 'react'

/**
 * useEditorSend — imperative send bridge between the proposal editor and the
 * chat panel's SDK context.
 *
 * Background (Task 10.2 outcome — Case B, deferred):
 * The @dev-streetbeat/chat SDK's useStreetBeatChat().sendMessage signature is:
 *   sendMessage(content: string | MessageInput[], attachments?: AttachmentContent[])
 * MessageInput = { content: string; hidden?: boolean }
 * There is no customData/metadata field on outgoing user messages.
 * useConversationFlow().continueThread() accepts thread-level metadata but not
 * per-message custom-data injection. ChatConfig has no message-level metadata.
 *
 * Workaround: block-menu intents are serialised into the prompt text so the
 * ACE editor assistant can read them. The assistant uses proposal_doc_get to
 * read the current block state — no custom-data transport is needed.
 *
 * Prompt format examples:
 *   "Shorten section §why-now"
 *   "Expand section §executive-summary"
 *   "Rewrite section §risks-mitigation"
 */

export type EditorMenuIntent = string

/** Return shape of useEditorSend */
export interface UseEditorSendResult {
  /**
   * Call this inside the ChatPanel (inside the SDK provider context) to
   * register the live sendMessage function. The hook stores the latest
   * reference in a ref so it is always up-to-date.
   */
  registerSend: (fn: (text: string) => void) => void

  /**
   * Invoke from the route's onMenuAction handler. Serialises intent + blockId
   * into a human-readable prompt and forwards it to the chat panel.
   *
   * If the chat panel has not registered a sender yet (e.g., still loading
   * the ACE session) the call is silently dropped.
   */
  sendEditorMessage: (intent: EditorMenuIntent, blockId: string) => void
}

/**
 * Map well-known intent identifiers to readable Italian/English directives.
 * Unknown intents fall back to a generic phrase.
 */
function intentToPrompt(intent: string, blockId: string): string {
  const section = `§${blockId}`
  switch (intent) {
    case 'shorten':
      return `Shorten section ${section}`
    case 'expand':
      return `Expand section ${section}`
    case 'rewrite':
      return `Rewrite section ${section}`
    case 'formalize':
      return `Make section ${section} more formal`
    case 'simplify':
      return `Simplify section ${section}`
    default:
      return `${intent} section ${section}`
  }
}

export function useEditorSend(): UseEditorSendResult {
  const sendRef = useRef<((text: string) => void) | null>(null)

  const registerSend = useCallback((fn: (text: string) => void) => {
    sendRef.current = fn
  }, [])

  const sendEditorMessage = useCallback((intent: EditorMenuIntent, blockId: string) => {
    if (!sendRef.current) return
    const prompt = intentToPrompt(intent, blockId)
    sendRef.current(prompt)
  }, [])

  return { registerSend, sendEditorMessage }
}
